import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'

import { ActionsDropdown } from 'components/shared/ActionsDropdown/ActionsDropdown'
import { IconButton } from 'components/shared/IconButton/IconButton'
import { ActionsCell } from 'components/shared/Table/common/Cells/ActionsCell/ActionsCell'
import { CandidateCell } from 'components/shared/Table/common/Cells/CandidateCell/CandidateCell'
import { JobProgressCell } from 'components/shared/Table/common/Cells/JobProgressCell/JobProgressCell'
import { RecommendationCell } from 'components/shared/Table/common/Cells/RecommendationCell/RecommendationCell'
import { ScoreCell } from 'components/shared/Table/common/Cells/ScoreCell/ScoreCell'
import { TextCell } from 'components/shared/Table/common/Cells/TextCell/TextCell'
import { Table } from 'components/shared/Table/Table'
import { CandidatesTable } from '../../../CandidatesTable/CandidatesTable'
import { getTooltipMessage } from 'components/pages/Jobs/JobDetails/helpers'
import { percentScore } from 'utils/stringFormat'
import { useCandidatesTable } from 'components/pages/Jobs/JobDetails/hooks'
import { getColumns } from './model'
import { HIRE_STATUSES } from '../../../model'

import { FileTextIcon, MessageCircleIcon } from 'components/shared/Icon/model'

import styles from './ResultsTable.module.scss'
import jobStyles from '../../../JobDetails.module.scss'

const mapSortingPropToQueryParam = {
  email: 'email',
  name: 'name',
  status: 'status',
  GMA: 'gma_score',
  Personality: 'personality_score',
  Interview: 'interview_score',
  'Ebber Final Score': 'final_score',
}

export const ResultsTable = ({
  projectId,
  search,
  filter,
  setFilter,
  tab,
  getActions,
  onInterview,
  onTestsReport,
  hasInterviewModule,
  handleFavorite,
  isInterviewAvailable,
  active,
}) => {
  const columns = getColumns()

  const status = React.useMemo(() => ['interviewed'], [])

  const {
    state: {
      table: {
        headers,
        model,
        rowSelection,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
      },
      pagination: { currentPage, pageCount, perPage },
    },
    handlers: {
      table: { toggleAllRowsSelectedHandler },
      pagination: { handlePage, handlePageSize },
    },
  } = useCandidatesTable({
    columns,
    mapSortingPropToQueryParam,
    projectId,
    search,
    filter,
    tab,
    status,
  })

  return (
    <CandidatesTable
      {...{
        active,
        headers,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
        currentPage,
        pageCount,
        perPage,
        toggleAllRowsSelectedHandler,
        handlePage,
        handlePageSize,
        filter,
        setFilter,
      }}
      placeholder={{
        title: t`No candidates on this stage`,
        text: t`Candidates will show up after tests and interview are completed`,
      }}
    >
      {R.map(
        ({
          id,
          original,
          getIsSelected,
          getIsSomeSelected,
          getToggleSelectedHandler,
        }) => {
          const {
            email,
            interview,
            name,
            recommendations,
            scores,
            selection_stage,
            hire_status,
            status,
            is_favourite,
            id: candidateId,
          } = original

          const actions = getActions({ candidate: original })

          return (
            <Table.Row
              key={id}
              type="body"
              onChange={getToggleSelectedHandler()}
              isChecked={getIsSelected()}
              isHighlighted={hire_status === HIRE_STATUSES.HIRED}
              isIndeterminate={getIsSomeSelected()}
              isSelected={rowSelection[id]}
              hasCheckbox
              isDisabled={!active}
              className={jobStyles.row}
            >
              <CandidateCell
                email={email}
                name={name}
                className={active ? jobStyles.favoriteIcon : undefined}
                isFavorite={is_favourite}
                setFavorite={handleFavorite(candidateId)}
                active={active}
              />
              <TextCell text={percentScore(scores['GMA'])} />
              <TextCell text={percentScore(scores['Personality'])} />
              <TextCell text={percentScore(scores['Interview'])} />
              <ScoreCell score={scores['Ebber Final Score']} />
              <RecommendationCell recommendation={recommendations?.final} />
              <JobProgressCell
                progress={selection_stage - 1}
                status={status}
                type="candidate"
                {...{ hasInterviewModule }}
              />
              <ActionsCell>
                <IconButton
                  className={styles.button}
                  icon={FileTextIcon}
                  size="medium"
                  variant="text"
                  onClick={() => onTestsReport({ candidate: original })}
                />
                <IconButton
                  className={styles.button}
                  icon={MessageCircleIcon}
                  size="medium"
                  variant="text"
                  message={getTooltipMessage({
                    hasInterview: Boolean(interview),
                    isInterviewAvailable,
                  })}
                  onClick={() => onInterview({ id: interview })}
                  isDisabled={
                    !isInterviewAvailable ||
                    R.and(isInterviewAvailable, R.isNil(interview)) ||
                    !active
                  }
                />
                <ActionsDropdown
                  anchorClassName={styles.button}
                  size="medium"
                  isDisabled={!active}
                  {...{ actions }}
                />
              </ActionsCell>
            </Table.Row>
          )
        },
        model.rows,
      )}
    </CandidatesTable>
  )
}
